"use client"

import clsx from "clsx"
import { memo, type MouseEvent, type ReactNode } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInteractiveProps } from "../../utils"

/**
 * 텍스트 링크 스타일 버튼
 *
 * @description
 * 배경 없이 텍스트만 표시되는 버튼입니다. hover 시 underline 효과가 나타납니다.
 * "더보기", "취소", 인라인 링크 액션 등에 사용합니다.
 *
 * @example
 * ```tsx
 * <TTextButton onClick={handleMore}>더보기</TTextButton>
 * <TTextButton color="primary">링크 텍스트</TTextButton>
 * <TTextButton color="secondary" size="small">부가 액션</TTextButton>
 * ```
 */
interface TTextButtonProps extends BaseInteractiveProps {
	/**
	 * 텍스트 색상
	 * @default "text"
	 * @ai-style "primary" → 녹색 텍스트, hover 시 진한 녹색 + underline
	 * @ai-style "text" → 기본 텍스트 색상, hover 시 녹색 + underline
	 * @ai-style "secondary" → 보조 텍스트 색상, hover 시 기본 색상 + underline
	 * @ai-style "tertiary" → 연한 텍스트, hover 시 보조 색상 + underline
	 */
	color?: "primary" | "text" | "secondary" | "tertiary"
	/**
	 * 텍스트 크기
	 * @default "medium"
	 * @ai-style "small" → text-sm
	 * @ai-style "medium" → text-base
	 * @ai-style "large" → text-lg
	 */
	size?: "small" | "medium" | "large"
	/** 버튼 텍스트 내용 */
	children: ReactNode
	/** 클릭 이벤트 핸들러 */
	onClick?: (event: MouseEvent<HTMLButtonElement>) => void
}

function TTextButtonInner(props: TTextButtonProps) {
	const {
		// HTML 속성
		id,
		className,
		// 스타일
		color = "text",
		size = "medium",
		// 상태
		disabled,
		// 데이터
		dataAttributes,
		children,
		// 핸들러
		onClick,
		...restProps
	} = props

	const dataProps = useDataAttributes(dataAttributes)

	return (
		<button {...restProps} type="button" id={id} className={clsx("TTextButton", color, size, className)} disabled={disabled} {...dataProps} onClick={onClick}>
			<span className="label">{children}</span>
		</button>
	)
}

TTextButtonInner.displayName = "TTextButton"

export const TTextButton = memo(TTextButtonInner)
